/**
 * Types locaux reproduisant la structure utilisée par l'algorithme.
 * Ne change pas le contrat; adapte si ton projet expose des types officiels.
 */

//import { DayLayoutFunction } from 'react-big-calendar';
import overlapDefault, { SlotMetrics, StyledEvent } from './overlap.utils';
import { CalendarEvent } from 'interfaces/calendar.interface';

export interface SlotRange {
  top: number;
  height: number;
}

export interface Accessors<CalendarEvent> {
  start( event: CalendarEvent ): Date;
  end( event: CalendarEvent ): Date;
  // allDay? etc. : omitted because not used in this algorithm
}


export interface FinalStyledEvent<CalendarEvent> {
  event: CalendarEvent;
  style: {
    top: number | string;
    height: number | string;
    left?: number | string;
    width?: string;
    xOffset: number | string;
  };
  friends?: StyledEvent<CalendarEvent>[];
  idx?: number;
  size?: number;
}



/**
 * Depth-first search to compute max index within a connected component.
 * - node: current StyledEvent
 * - maxIdx: current known maximum index
 * - visited: list of visited nodes (accumulating references)
 */
function getMaxIdxDFS<CalendarEvent>(
  node: StyledEvent<CalendarEvent>,
  maxIdx: number,
  visited: StyledEvent<CalendarEvent>[]
): number {
  for ( let i = 0; i < ( node.friends ?? [] ).length; ++i ) {
    const friend = node.friends![ i ];

    if ( visited.indexOf( friend ) === -1 ) {
      maxIdx = maxIdx > ( friend.idx ?? 0 ) ? maxIdx : ( friend.idx ?? 0 );

      // push reference to visited
      visited.push( friend );

      const newIdx = getMaxIdxDFS( friend, maxIdx, visited );
      maxIdx = maxIdx > newIdx ? maxIdx : newIdx;
    }
  }
  return maxIdx;
}

/**
 * Default export: same algorithm as original JS but typed.
 *
 * Note:
 * - CalendarEvent must at least have start/end Date (matching what calendar expects).
 * - We keep the algorithm intact; the result type is StyledEvent<CalendarEvent>[].
 */
export const dayLayoutAlgorithm = ( {
  events,
  minimumStartDifference,
  slotMetrics,
  accessors,
  offset
}: {
  events: readonly CalendarEvent[];
  minimumStartDifference: number;
  slotMetrics: SlotMetrics;
  accessors: Accessors<CalendarEvent>;
  offset: number
} ): FinalStyledEvent<CalendarEvent>[] => {
  // call overlap (module may be untyped). Cast the result to the expected typed array.
  // If ./overlap is already typed properly you can remove the assertion.

  const styledEvents = overlapDefault( {
    events,
    minimumStartDifference,
    slotMetrics,
    accessors,
  } ) as StyledEvent<CalendarEvent>[];

  // sort
  styledEvents.sort( ( a, b ) => {
    const sa = a.style;
    const sb = b.style;
    if ( sa.top !== sb.top ) {
      return sa.top > sb.top ? 1 : -1;
    }
    else if ( sa.height !== sb.height ) {
      return sa.top + sa.height < sb.top + sb.height ? 1 : -1;
    }
    else {return 0;}
  } );

  // initialize friends / cleanup fields
  for ( let i = 0; i < styledEvents.length; ++i ) {
    styledEvents[ i ].friends = [];

    // delete left if present (was duplicated in original)
    delete styledEvents[ i ].style.left;
    delete styledEvents[ i ].idx;
    delete styledEvents[ i ].size;
  }

  // build friendship graph (overlaps in vertical axis)
  for ( let i = 0; i < styledEvents.length - 1; ++i ) {
    const se1 = styledEvents[ i ];
    const y1 = se1.style.top;
    const y2 = y1 + se1.style.height;

    for ( let j = i + 1; j < styledEvents.length; ++j ) {
      const se2 = styledEvents[ j ];
      const y3 = se2.style.top;
      const y4 = y3 + se2.style.height;

      // same overlap conditions as original
      if (
        ( y3 >= y1 && y4 <= y2 ) ||
        ( y4 > y1 && y4 <= y2 ) ||
        ( y3 >= y1 && y3 < y2 )
      ) {
        // TODO : hashmap would be effective for performance
        se1.friends!.push( se2 );
        se2.friends!.push( se1 );
      }
    }
  }

  // reserve indices using bitmap (0..99)
  for ( let i = 0; i < styledEvents.length; ++i ) {
    const se = styledEvents[ i ];
    const bitmap: number[] = [];
    for ( let b = 0; b < 100; ++b ) {
      bitmap.push( 1 );
    } // 1 means available

    for ( let f = 0; f < ( se.friends ?? [] ).length; ++f ) {
      const idx = se.friends![ f ].idx;
      if ( idx !== undefined ) {
        bitmap[ idx ] = 0;
      }
    }

    se.idx = bitmap.indexOf( 1 );
  }

  // compute component maximum index and assign size
  for ( let i = 0; i < styledEvents.length; ++i ) {
    if ( !styledEvents[ i ].size ) {
      const allFriends: StyledEvent<CalendarEvent>[] = [];

      // getMaxIdxDFS will fill visited via pushes, but it expects starting node's friends to be visited
      // push starting node into visited so we get consistent component
      allFriends.push( styledEvents[ i ] );

      if ( styledEvents[ i ].event.allDayIndicator ) {
        styledEvents[ i ].size = 5;
      }
      else {
        const maxIdx = getMaxIdxDFS(
          styledEvents[ i ],
          styledEvents[ i ].idx ?? 0,
          allFriends
        );

        const size = 100 / ( maxIdx + 1 );
        styledEvents[ i ].size = size;

        for ( let f = 0; f < allFriends.length; ++f ) {
          allFriends[ f ].size = size;
        }
      }
    }
  }

  const finalStyledEvent: FinalStyledEvent<CalendarEvent>[] = styledEvents;

  // Final layout
  for ( let i = 0; i < finalStyledEvent.length; ++i ) {
    const e = finalStyledEvent[ i ];

    // e.idx and e.size must exist at this point per algorithm
    const idx = e.idx ?? 0;
    const size = e.size ?? 0;
    e.style.left = idx * ( size );

    // avoid useless math if it is allDay event
    if ( e.event.allDayIndicator ) {
      e.style.width = '0.5rem';
      e.style.height = `calc(${e.style.height}% - 2px)`;
      e.style.xOffset = `${e.style.left * 0.15}rem`;
    }

    // do math else
    else {
      // stretch to maximum
      let maxIdx = 0;
      for ( let f = 0; f < ( e.friends ?? [] ).length; ++f ) {
        const friendIdx = e.friends![ f ].idx ?? 0;
        maxIdx = maxIdx > friendIdx ? maxIdx : friendIdx;
      }

      const ratio = 100 / ( e.size ?? 1 );
      const offsetWidth = ( ( offset * 0.75 ) / ratio );
      const calcOffset = ( offset * 0.75 ) - ( offsetWidth * idx );
      const padding = ( idx === 0 ) ? 0 : 3;

      if ( maxIdx <= idx ) {
        e.size = 100 - idx * ( e.size ?? 0 );
      }

      e.style.width = `calc(${e.size}% - ${padding}px - ${offsetWidth}rem) `;
      e.style.height = `calc(${e.style.height}% - 2px)`;
      e.style.xOffset = `calc(${e.style.left}% + ${padding}px + ${calcOffset}rem )`;
    }
  }

  return finalStyledEvent;
};
